import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Select,
  SelectItem,
  DatePicker,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
} from "@heroui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  Download,
  Filter,
  BarChart2,
  Percent,
  TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { parseDate } from "@internationalized/date";

import { reportsService } from "@/services/reportsService";
import { menuService } from "@/services/menuService";
import { formatCurrency } from "@/utils/formatters";

export default function ItemsReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
    // 30 days ago
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [reportData, setReportData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewType, setViewType] = useState<"table" | "chart">("table");
  const [sortField, setSortField] = useState("quantity");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const data = await menuService.getCategories();

      setCategories(data);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const params = {
        start_date: startDate,
        end_date: endDate,
        category_id: selectedCategory || undefined,
        limit: 100, // Get a high limit for all data
      };

      const data = await reportsService.getItemsSoldReport(params);

      // Sort data based on the selected field
      const sortedData = [...data].sort((a, b) => {
        const valueA = a[sortField];
        const valueB = b[sortField];

        if (sortDirection === "asc") {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });

      setReportData(sortedData);
    } catch (err) {
      console.error("Error loading items report:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/reports");
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    setSelectedCategory(value ? parseInt(value) : null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSortChange = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }

    // Re-sort data
    const sortedData = [...reportData].sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      if (
        (sortField === field && sortDirection === "desc") ||
        sortField !== field
      ) {
        return valueA - valueB; // asc
      } else {
        return valueB - valueA; // desc
      }
    });

    setReportData(sortedData);
  };

  const handleExportCSV = () => {
    // CSV export implementation
    alert("CSV export will be implemented here");
  };

  // Calculate the total of all sales for percentage calculations
  const totalQuantity = reportData.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const totalRevenue = reportData.reduce((sum, item) => sum + item.revenue, 0);

  // Data pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = reportData.slice(startIndex, endIndex);

  // Top 10 for the chart
  const topItemsForChart = reportData.slice(0, 10);

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Sold Items Report</h1>
        </div>
        <div className="flex gap-2">
          <Button
            color="primary"
            startContent={<Download size={16} />}
            variant="flat"
            onPress={handleExportCSV}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <DatePicker
            label="Start Date"
            value={parseDate(startDate)}
            onChange={(date) => setStartDate(date?.toString() || "")}
          />
          <DatePicker
            label="End Date"
            value={parseDate(endDate)}
            onChange={(date) => setEndDate(date?.toString() || "")}
          />
          <Select
            label="Category"
            placeholder="All Categories"
            onChange={handleCategoryChange}
          >
            <SelectItem key="all" textValue="Todas las categorías">
              Todas las categorías
            </SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} textValue={category.name}>
                {category.name}
              </SelectItem>
            )) as any}
          </Select>
          <div className="flex items-end gap-2">
            <Button
              isIconOnly
              color={viewType === "table" ? "primary" : "default"}
              variant={viewType === "table" ? "solid" : "flat"}
              onPress={() => setViewType("table")}
            >
              <Filter size={16} />
            </Button>
            <Button
              isIconOnly
              color={viewType === "chart" ? "primary" : "default"}
              variant={viewType === "chart" ? "solid" : "flat"}
              onPress={() => setViewType("chart")}
            >
              <BarChart2 size={16} />
            </Button>
            <Button color="primary" onPress={fetchReportData}>
              Update
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-8">
            <Spinner size="lg" />
          </div>
        ) : reportData.length === 0 ? (
          <div className="text-center p-6 border border-dashed rounded-md">
            <p className="text-default-500">
              No data available for the selected period
            </p>
          </div>
        ) : viewType === "table" ? (
          <div>
            <Table
              aria-label="Sold items table"
              bottomContent={
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-small text-default-400">
                      Rows per page:
                    </span>
                    <select
                      className="bg-transparent text-small text-default-400 border border-default-400 rounded-md px-2 py-1"
                      value={itemsPerPage}
                      onChange={(e) =>
                        handleItemsPerPageChange(Number(e.target.value))
                      }
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <Pagination
                    showControls
                    color="primary"
                    page={currentPage}
                    total={Math.ceil(reportData.length / itemsPerPage)}
                    onChange={handlePageChange}
                  />
                </div>
              }
            >
              <TableHeader>
                <TableColumn
                  className="cursor-pointer"
                  onClick={() => handleSortChange("name")}
                >
                  PRODUCT
                </TableColumn>
                <TableColumn
                  className="cursor-pointer"
                  onClick={() => handleSortChange("category")}
                >
                  CATEGORY
                </TableColumn>
                <TableColumn
                  className="cursor-pointer text-right"
                  onClick={() => handleSortChange("quantity")}
                >
                  UNITS <TrendingUp className="inline" size={14} />
                </TableColumn>
                <TableColumn
                  className="cursor-pointer text-right"
                  onClick={() => handleSortChange("percentage")}
                >
                  % OF TOTAL <Percent className="inline" size={14} />
                </TableColumn>
                <TableColumn
                  className="cursor-pointer text-right"
                  onClick={() => handleSortChange("revenue")}
                >
                  REVENUE
                </TableColumn>
              </TableHeader>
              <TableBody>
                {currentItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell className="text-right">
                      {item.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {((item.quantity / totalQuantity) * 100).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="h-96">
            <h3 className="text-lg font-medium mb-4">
              Top 10 Sold Items
            </h3>
            <ResponsiveContainer height="100%" width="100%">
              <BarChart
                data={topItemsForChart}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 60,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  angle={-45}
                  dataKey="name"
                  height={80}
                  textAnchor="end"
                />
                <YAxis orientation="left" stroke="#8884d8" yAxisId="left" />
                <YAxis orientation="right" stroke="#82ca9d" yAxisId="right" />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="quantity"
                  name="Units"
                  fill="#8884d8"
                />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill="#82ca9d"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <h3 className="text-sm text-default-500">Total Units</h3>
              <p className="text-2xl font-semibold">{totalQuantity}</p>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <h3 className="text-sm text-default-500">Total Revenue</h3>
              <p className="text-2xl font-semibold">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <h3 className="text-sm text-default-500">Different Products</h3>
              <p className="text-2xl font-semibold">{reportData.length}</p>
            </div>
          </Card>
        </div>
      </Card>
    </div>
  );
}
